// Where a step's target is on screen, measured once per frame. The overlay
// draws from this: a ring + card when the target is in view, a faded card
// when something covers it, the dock pill when it is out of view or can't be
// found.

import type { EngineState } from '@/editor/engine/types';
import { anchorSelector, TOUR_ANCHORS, type TourTarget, targetElements } from './tourAnchors';

/** The paper's sticky toolbar: a target scrolled under it is out of view. */
const STICKY_TOOLBAR_PX = 32;

export type Placement =
    /** Resolved and in the paper's viewport. */
    | 'visible'
    /** In view, but another surface sits on top of it. */
    | 'covered'
    /** Resolved, but scrolled away or virtualized out. */
    | 'offscreen'
    /** Nothing to point at: the resolver found no target, or its anchor is not rendered. */
    | 'unresolved';

export interface TargetMeasure {
    placement: Placement;
    /** The union of the target's boxes; `null` unless it is in view. */
    rect: DOMRect | null;
}

/** Marks the tutorial's own surfaces, which never count as covering a target. */
export const TOUR_OVERLAY_ATTR = 'data-tour-overlay';

function unionRect(elements: HTMLElement[]): DOMRect | null {
    let top = Number.POSITIVE_INFINITY;
    let left = Number.POSITIVE_INFINITY;
    let right = Number.NEGATIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;
    for (const element of elements) {
        const rect = element.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue;
        top = Math.min(top, rect.top);
        left = Math.min(left, rect.left);
        right = Math.max(right, rect.right);
        bottom = Math.max(bottom, rect.bottom);
    }
    return top === Number.POSITIVE_INFINITY ? null : new DOMRect(left, top, right - left, bottom - top);
}

export function measureTarget(target: TourTarget | null, state: EngineState): TargetMeasure {
    if (target === null) return { placement: 'unresolved', rect: null };
    const elements = targetElements(target, state);
    const rect = unionRect(elements);
    if (rect === null) return { placement: target.kind === 'anchor' ? 'unresolved' : 'offscreen', rect: null };

    // A target in the paper is in view inside the paper's viewport, below its
    // sticky toolbar; one outside it (the header, the navigator, a dialog)
    // anywhere in the window.
    const scroller = document.querySelector(anchorSelector(TOUR_ANCHORS.paperScroller));
    const inPaper = target.kind !== 'anchor' || (scroller !== null && elements.some(element => scroller.contains(element)));
    const view = inPaper ? scroller?.getBoundingClientRect() : undefined;
    const viewTop = inPaper ? (view?.top ?? 0) + STICKY_TOOLBAR_PX : 0;
    const viewBottom = view?.bottom ?? window.innerHeight;
    if (rect.bottom <= viewTop || rect.top >= viewBottom) return { placement: 'offscreen', rect: null };

    // Probe near the leading edge of the part that is in view: the text a
    // reader looks at, not the middle of a tall block.
    const x = rect.left + Math.min(24, rect.width / 2);
    const visibleTop = Math.max(rect.top, viewTop);
    const y = Math.min(visibleTop + Math.min(12, rect.height / 2), viewBottom - 1);
    const hit = document.elementFromPoint(x, y);
    // A modal Radix popover sets `pointer-events: none` on the body, so the
    // probe falls through the page to <html>: nothing is painted over the
    // target, the page is only click-locked while the popover is open (the
    // visibility popover is how two Visibility steps are done).
    const fellThrough = hit === document.documentElement || hit === document.body;
    const covered =
        hit !== null && !fellThrough && !elements.some(element => element.contains(hit)) && hit.closest(`[${TOUR_OVERLAY_ATTR}]`) === null;
    return { placement: covered ? 'covered' : 'visible', rect };
}
